<template>
  <div class="home-view">
    <!-- Header section -->
    <van-sticky z-index="99">
      <div class="header">
        <div class="user-profile">
          <div class="avatar-wrapper">
            <van-image
                round
                width="48px"
                height="48px"
                :src="imageUrl(user.avatar)"
            />
            <div class="status-indicator"></div>
          </div>
          <div class="user-info">
            <span class="greeting">Good Morning,</span>
            <span class="uid-tag">UID: {{ user.userId }}</span>
          </div>
        </div>
        <div class="header-actions">
          <van-icon name="bell" size="24" class="action-icon"/>
        </div>
      </div>
    </van-sticky>

    <!-- Banner section -->
    <div class="banner-section">
      <van-swipe
          class="modern-swipe"
          :autoplay="4000"
          indicator-color="#ff9f00"
          @change="onChange"
      >
        <van-swipe-item v-for="(item, idx) in swipeItems" :key="idx">
          <div
              v-if="item.type === 'pwd'"
              class="banner-card pwd-remind-banner"
              role="button"
              tabindex="0"
              @click="handlePwdRemindBanner"
              @keydown.enter="handlePwdRemindBanner"
          >
            <van-icon name="shield-o" size="36" class="pwd-remind-icon"/>
            <p class="pwd-remind-text">{{ item.text }}</p>
          </div>
          <div v-else class="banner-card">
            <van-image fit="cover" :src="item.src" class="banner-img"/>
            <div class="banner-overlay"></div>
          </div>
        </van-swipe-item>
      </van-swipe>
    </div>

    <!-- Contact Tasks Quick Access -->
    <div class="quick-access-section">
      <div class="quick-access-grid">
        <div class="quick-access-card" @click="handleContactTasks">
          <div class="quick-access-icon-wrapper">
            <van-icon name="orders-o" size="32" color="#ff9f00"/>
          </div>
          <span class="quick-access-text">Contact Tasks</span>
        </div>
        <div class="quick-access-card" @click="handleContactTaskOrders">
          <div class="quick-access-icon-wrapper">
            <van-icon name="list" size="32" color="#ff9f00"/>
          </div>
          <span class="quick-access-text">My Task Orders</span>
        </div>
      </div>
    </div>

    <!-- Tasks section -->
    <div class="tasks-section">
      <div class="section-header">
        <h2 class="section-title">Available Tasks</h2>
        <div class="filter-actions">
          <van-dropdown-menu class="custom-dropdown">
            <van-dropdown-item
                v-model="selectedPaytype"
                :options="taskOptions"
                @change="handleSelectTasks"
                :title="payTypeTitle"
            />
          </van-dropdown-menu>
          <van-button
              icon="replay"
              size="small"
              plain
              round
              type="primary"
              @click="handleSelectTasks(null)"
              class="refresh-btn"
          />
        </div>
      </div>

      <van-list
          v-model:loading="loading"
          :finished="finished"
          loading-text="Loading..."
          finished-text="No more tasks available"
          @load="onLoad"
          class="task-list"
      >
        <div v-if="loading && orderlist.length === 0" class="skeletons">
          <van-skeleton v-for="i in 3" :key="i" title avatar :row="3" style="margin-bottom: 20px"/>
        </div>

        <div v-for="(task, index) in orderlist" :key="index" class="task-card" :class="{ 'task-card-urgent': isUrgentOrder(task) }">
          <div class="card-header-top">
            <div class="task-badge" :class="task.payType.toLowerCase()">
              {{ task.payType }}
            </div>
            <div class="amount-header">
              <span class="amount-value">{{ task.showAmount }} {{ task.currency }}</span>
            </div>
          </div>
          <div class="task-content">
            <div class="task-details">
              <div class="receiver-section">
                <div class="receiver-info">
                  <span class="info-label">{{ getReceiveListRecipientLabel(task) }}</span>
                  <h3 class="receiver-name">{{ getReceiveListRecipientDisplay(task) }}</h3>
                </div>
                <van-button
                    class="receive-btn"
                    type="primary"
                    round
                    :disabled="shouldHideAccountNo(task)"
                    @click="handleReceive(task)"
                >
                  Receive
                </van-button>
              </div>
              <div v-if="task.bankName" class="bank-section">
                <span class="info-label">Bank Name</span>
                <p class="bank-name">
                  <van-icon name="balance-o"/>
                  {{ task.bankName }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </van-list>
    </div>

    <!-- Confirm Popup -->
    <van-dialog
        v-model:show="showReceiveConfirm"
        title="Confirm Order"
        show-cancel-button
        cancel-button-text="Cancel"
        confirm-button-text="Confirm"
        @confirm="confirmReceive"
        @cancel="cancelReceive"
        confirm-button-color="#ff9f00"
        class="confirm-dialog"
    >
      <div class="confirm-content">
        <p class="confirm-message">Are you sure you want to accept this order?</p>
        <div class="order-info-card">
          <div class="order-info-label">Order No</div>
          <div class="order-info-value">{{ selectedTask?.orderNo }}</div>
        </div>
      </div>
    </van-dialog>

    <!-- Success Toast -->
    <van-toast
        v-model:show="showSuccessToast"
        icon="success"
        message="Order Received!"
    />
  </div>
</template>

<script setup>
// ... (script remains largely the same, but I'll add some tweaks for the new template)
import {onMounted, ref} from 'vue'
import {useRouter} from 'vue-router'
import {getTips, getUserInfo, getWithdrawType, receive, receivlist,} from '@/utils/api'
import {
  ensureReceiverAccountRemarkHideKeywords,
  getReceiveListRecipientDisplay,
  getReceiveListRecipientLabel,
  shouldHideAccountNo,
} from '@/utils/receiverAccountRemarkHide'
import {imageUrl} from '@/utils'
import {showFailToast} from 'vant'
import {maybeShowPwdRemindDialog, PWD_REMIND_MESSAGE} from '@/utils/pwdRemind'
import banner1 from '../assets/banner1.png'
import banner2 from '../assets/banner2.png'

const router = useRouter()
const user = ref({})
const orderlist = ref([])
const showReceiveConfirm = ref(false)
const showSuccessToast = ref(false)
const selectedTask = ref(null)
const loading = ref(false)
const finished = ref(false)
const index = ref(0)
const swipeItems = ref([
  {type: 'img', src: banner1},
  {type: 'img', src: banner2},
])
const pwdRemind = ref(false)

const queryParams = ref({
  pageNum: 0,
  pageSize: 15,
  orderByColumn: 'createTime asc, receiverName',
  isAsc: 'asc',
  currency: null,
  userId: null,
  withdrawAmount: null,
  receiverName: null,
  receiverAccount: null,
  sourceOrderNo: null,
  orderNo: null,
  withdrawState: null,
  payId: null,
  payType: null,
})

const taskOptions = ref([])
const taskTypeList = ref([])
const payTypeTitle = ref('Payment')
const selectedPaytype = ref(null)

const fetchUserData = async () => {
  try {
    const userInfoResponse = await getUserInfo()
    user.value = userInfoResponse.data
    pwdRemind.value = !!user.value?.pwdRemindPo
    const baseBanners = [
      {type: 'img', src: banner1},
      {type: 'img', src: banner2},
    ]
    swipeItems.value = pwdRemind.value
      ? [{type: 'pwd', text: PWD_REMIND_MESSAGE}, ...baseBanners]
      : baseBanners
    maybeShowPwdRemindDialog(pwdRemind.value, router)
    queryParams.value.pageNum = 1
    const listType = await getWithdrawType()
    taskTypeList.value = listType.data.filter((s) => user.value.receiveOrderPayTypes.indexOf(s.dictValue) > -1)
    taskOptions.value = taskTypeList.value.map((item) => ({
      text: item.dictLabel,
      value: item.dictValue,
    }))
    taskOptions.value.splice(0, 0, {text: 'ALL', value: null})
  } catch (error) {
    console.error('Error fetching user data:', error)
  }
}

const handlePwdRemindBanner = () => {
  router.push('/resetpwd')
}

const onChange = (i) => {
  index.value = i
}

const onLoad = async () => {
  try {
    await ensureReceiverAccountRemarkHideKeywords()
    loading.value = true
    queryParams.value.pageNum++

    const taskListResponse = await receivlist(queryParams.value)

    if (taskListResponse.rows && taskListResponse.rows.length > 0) {
      // 检测 MyBatis reasonable=true 的情况：如果返回的数据第一条已存在于列表中，说明已经到底了
      const firstNewItemId = taskListResponse.rows[0]?.id
      const isDuplicate = orderlist.value.length > 0 && orderlist.value.some(item => item.id === firstNewItemId)

      if (isDuplicate) {
        // 返回的数据与已有数据重复，说明已经到底了（reasonable=true导致返回了第一页）
        finished.value = true
      } else {
        // 正常情况：添加新数据
        orderlist.value = [...orderlist.value, ...taskListResponse.rows]
        // 如果返回的数据少于pageSize，说明已经到底了
        if (taskListResponse.rows.length < queryParams.value.pageSize) {
          finished.value = true
        }
      }
    } else {
      finished.value = true
    }
  } catch (error) {
    if (error.message && (error.message === 'No permission' || error.message.indexOf("Too many requests"))) {
      finished.value = true
      loading.value = false
      showFailToast(error.message)
      return
    }
    console.error('Error loading data:', error)
  } finally {
    loading.value = false
  }
}

const handleReceive = (task) => {
  if (shouldHideAccountNo(task)) {
    showFailToast('This order is not available to receive')
    return
  }
  selectedTask.value = task
  showReceiveConfirm.value = true
}

const handleSelectTasks = async (task) => {
  queryParams.value.payType = task
  payTypeTitle.value = taskOptions.value.find(o => o.value === task)?.text || 'Payment'
  queryParams.value.pageNum = 0
  orderlist.value = []
  finished.value = false
  onLoad()
}

const confirmReceive = async () => {
  if (!selectedTask.value) return
  if (shouldHideAccountNo(selectedTask.value)) {
    showFailToast('This order is not available to receive')
    showReceiveConfirm.value = false
    selectedTask.value = null
    return
  }
  try {
    await receive(selectedTask.value)
    await fetchUserData()
    showSuccessToast.value = true
    orderlist.value = []
    queryParams.value.pageNum = 0
    onLoad()
  } catch (error) {
    showFailToast(getTips(error))
  } finally {
    showReceiveConfirm.value = false
    selectedTask.value = null
  }
}

const cancelReceive = () => {
  showReceiveConfirm.value = false
  selectedTask.value = null
}

const handleContactTasks = () => {
  window.$router.push('/contact-task')
}

const handleContactTaskOrders = () => {
  window.$router.push('/contact-task-order')
}

// 判断订单是否紧急（当前时间减去创建时间大于8小时）
const isUrgentOrder = (task) => {
  if (!task.createTime) return false
  const createTime = new Date(task.createTime).getTime()
  const currentTime = Date.now()
  const eightHours = 8 * 60 * 60 * 1000 // 8小时的毫秒数
  return (currentTime - createTime) > eightHours
}

onMounted(() => {
  ensureReceiverAccountRemarkHideKeywords()
  fetchUserData()
})
</script>

<style scoped>
.home-view {
  background-color: #f8f9fa;
  min-height: 100vh;
  padding: 0 0 100px;
  padding-bottom: calc(100px + env(safe-area-inset-bottom));
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border-bottom-left-radius: 20px;
  border-bottom-right-radius: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
}

.user-profile {
  display: flex;
  align-items: center;
  gap: 12px;
}

.avatar-wrapper {
  position: relative;
}

.status-indicator {
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 12px;
  height: 12px;
  background: #4cd964;
  border: 2px solid #fff;
  border-radius: 50%;
}

.user-info {
  display: flex;
  flex-direction: column;
}

.greeting {
  font-size: 13px;
  color: #8e8e93;
}

.uid-tag {
  font-size: 16px;
  font-weight: 700;
  color: #1a1a1a;
}

.action-icon {
  color: #1a1a1a;
  padding: 8px;
  background: #f2f2f7;
  border-radius: 12px;
}

.banner-section {
  padding: 16px 12px;
}

.modern-swipe {
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
}

.banner-card {
  position: relative;
  height: 160px;
}

.banner-img {
  width: 100%;
  height: 100%;
}

.banner-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 50%;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.4), transparent);
}

.pwd-remind-banner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 24px 20px;
  background: linear-gradient(135deg, #2c3e50 0%, #1a252f 100%);
  cursor: pointer;
}

.pwd-remind-icon {
  color: #ff9f00;
}

.pwd-remind-text {
  margin: 0;
  color: #fff;
  font-size: 15px;
  line-height: 1.5;
  text-align: center;
  max-width: 90%;
}

.quick-access-section {
  padding: 16px 12px;
}

.quick-access-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.quick-access-card {
  background: white;
  border-radius: 16px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(255, 159, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
}

.quick-access-card:active {
  transform: scale(0.98);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  box-shadow: 0 4px 16px rgba(255, 159, 0, 0.15);
  border-color: rgba(255, 159, 0, 0.2);
}

.quick-access-icon-wrapper {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #fff5e6 0%, #ffe8cc 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.quick-access-card:active .quick-access-icon-wrapper {
  background: linear-gradient(135deg, #ffd699 0%, #ffcc80 100%);
  transform: scale(1.05);
}

.quick-access-text {
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
  text-align: center;
}

.tasks-section {
  padding: 0 12px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
  padding: 0 8px;
}

.section-title {
  font-size: 20px;
  font-weight: 800;
  color: #1a1a1a;
  margin: 0;
  letter-spacing: -0.3px;
}

.filter-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.custom-dropdown {
  width: auto;
}

:deep(.van-dropdown-menu__bar) {
  height: 36px;
  background: transparent;
  box-shadow: none;
}

:deep(.van-dropdown-menu__title) {
  font-size: 14px;
  font-weight: 600;
  color: #ff9f00;
}

.refresh-btn {
  border: none;
  background: #fff;
  color: #ff9f00;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.task-list {
  padding-bottom: 20px;
}

.task-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  border-color: rgba(255, 159, 0, 0.1);
}

.task-card {
  background: #fff;
  border-radius: 12px;
  padding: 12px;
  margin-bottom: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.03);
  position: relative;
  border: 1px solid rgba(0, 0, 0, 0.02);
  transition: all 0.3s ease;
  overflow: visible;
}

.task-card:active {
  transform: scale(0.98);
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.05);
}

/* 紧急订单特殊样式（创建时间早于到期时间8小时） */
.task-card-urgent {
  border: 2px solid #FF3B30;
  background: linear-gradient(135deg, rgba(255, 59, 48, 0.08) 0%, #fff 50%, #fff 100%);
  box-shadow: 0 2px 12px rgba(255, 59, 48, 0.2), 0 2px 10px rgba(0, 0, 0, 0.03);
}

.task-card-urgent:hover {
  border-color: #FF3B30;
  box-shadow: 0 4px 16px rgba(255, 59, 48, 0.3), 0 2px 10px rgba(0, 0, 0, 0.05);
}

.card-header-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.task-badge {
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  display: inline-flex;
  align-items: center;
  gap: 3px;
  flex-shrink: 0;
}

.task-badge.bank {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  color: #1976d2;
}

.task-badge.crypto {
  background: linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%);
  color: #7b1fa2;
}

.task-badge.default {
  background: linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%);
  color: #616161;
}

.task-content {
  margin: 0;
}

.task-details {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.receiver-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding-top: 0;
}

.receiver-info {
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1;
  min-width: 0;
}

.info-label {
  font-size: 9px;
  font-weight: 600;
  color: #8e8e93;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  line-height: 1.2;
}

.receiver-name {
  font-size: 15px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
  line-height: 1.3;
  word-break: break-word;
}

.bank-section {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.bank-name {
  font-size: 12px;
  color: #1a1a1a;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  line-height: 1.3;
  font-weight: 600;
}

.bank-name .van-icon {
  font-size: 14px;
  color: #8e8e93;
}

.amount-header {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.amount-value {
  font-size: 16px;
  font-weight: 800;
  color: #ff9f00;
  white-space: nowrap;
  letter-spacing: -0.3px;
  line-height: 1.2;
}

.receive-btn {
  height: 30px;
  padding: 0 14px;
  background: linear-gradient(135deg, #ff9f00 0%, #ffb74d 100%);
  border: none;
  font-weight: 700;
  font-size: 11px;
  flex-shrink: 0;
  min-width: 70px;
  box-shadow: 0 2px 8px rgba(255, 159, 0, 0.25);
  transition: all 0.3s ease;
}

.receive-btn:active {
  transform: scale(0.95);
  box-shadow: 0 1px 4px rgba(255, 159, 0, 0.3);
}

:deep(.confirm-dialog) {
  border-radius: 20px;
  overflow: hidden;
}

:deep(.confirm-dialog .van-dialog__header) {
  padding: 20px 20px 0;
  font-size: 18px;
  font-weight: 700;
  color: #1a1a1a;
}

:deep(.confirm-dialog .van-dialog__content) {
  padding: 0 20px 20px;
}

.confirm-content {
  padding: 16px 0;
  text-align: center;
}

.confirm-message {
  font-size: 14px;
  color: #666;
  margin: 0 0 16px;
  line-height: 1.5;
}

.order-info-card {
  background: #f8f9fb;
  padding: 12px 16px;
  border-radius: 12px;
  text-align: left;
}

.order-info-label {
  font-size: 10px;
  font-weight: 600;
  color: #8e8e93;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
}

.order-info-value {
  font-size: 13px;
  font-weight: 600;
  color: #1a1a1a;
  word-break: break-all;
  line-height: 1.4;
}

:deep(.confirm-dialog .van-dialog__footer) {
  padding: 0 20px 20px;
  border-top: none;
}

:deep(.confirm-dialog .van-button--default) {
  border-color: #e5e5e5;
  color: #666;
  font-weight: 600;
}

:deep(.confirm-dialog .van-button--primary) {
  background: linear-gradient(135deg, #ff9f00 0%, #ffb74d 100%);
  border: none;
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(255, 159, 0, 0.25);
}
</style>
